import { NotificationManager } from 'react-notifications';
import axios from 'axios';
import isArray from 'lodash/isArray';
import { IFlowData } from 'src/components/ui/Form/types';
import { ISelectOptions } from 'src/components/ui/Select/createSelect.types';
import { IQueryParams } from 'src/types';
import { attempt } from 'src/utils/attempts';

import { IPaymentInvoiceHistory, IPaymentOrderId, IPaymentOrderListData } from './types';

export const fetchPaymentOrdersRequest = async ({
  refillExternalIdentity,
  amountFrom,
  amountTo,
  createdAtFrom,
  createdAtTo,
  merchantIds,
  merchantAccountIds,
  statuses,
  metaFilter,
  page,
  size,
  sort,
  ...params
}: IQueryParams): Promise<IPaymentOrderListData | null> => {
  try {
    const filters = { ...params };

    if (amountFrom || amountTo) {
      filters.amountFilter = {
        amountFrom,
        amountTo,
      };
    }
    if (createdAtFrom || createdAtTo) {
      filters.dateFilter = {
        createdAtFrom,
        createdAtTo,
      };
    }
    if (merchantIds) {
      filters.merchantIds = isArray(merchantIds) ? merchantIds : [merchantIds];
    }
    if (merchantAccountIds) {
      filters.merchantAccountIds = isArray(merchantAccountIds) ? merchantAccountIds : [merchantAccountIds];
    }
    if (statuses) {
      filters.statuses = isArray(statuses) ? statuses : [statuses];
    }
    if (refillExternalIdentity) {
      filters.refillExternalIdentity = refillExternalIdentity;
    }
    if (metaFilter) {
      filters.metaFilter = attempt(() => JSON.parse(metaFilter), {});
    }

    const res = await axios.post('api/v1/payment/payment-order-search', filters, { params: { page, size, sort } });
    return res.data;
  } catch (error) {
    NotificationManager.error("Can't fetch payment orders");
    return null;
  }
};

export const fetchPaymentOrderByIdRequest = async (id: string): Promise<IPaymentOrderId | undefined> => {
  try {
    const res = await axios.get(`api/v1/payment/payment-order-search/${id}`);
    return res.data;
  } catch (error) {
    NotificationManager.error("Can't fetch payment order");
    return;
  }
};

export const fetchPaymentOrderHistoryRequest = async ({
  orderIdentity: paymentOrderIdentity,
}: IFlowData): Promise<IPaymentInvoiceHistory | null> => {
  try {
    const res = await axios.post('api/v1/payment/invoice-history', { paymentOrderIdentity });

    return res.data;
  } catch (error) {
    NotificationManager.error("Can't fetch invoice history");
    return null;
  }
};

export const fetchDictPaymentStatus = async (data: IFlowData): Promise<ISelectOptions> => {
  const res = await axios.get('api/v1/payment/search-filter/status', data);
  return res.data?.map((status: string) => ({ value: status, label: status }));
};

export const fetchDictPaymentCurrency = async (data: IFlowData): Promise<ISelectOptions> => {
  const res = await axios.get('api/v1/payment/search-filter/currency', data);
  return res.data?.map((currency: string) => ({ value: currency, label: currency }));
};

export const fetchDictPaymentMerchantAccount = async (data: IFlowData): Promise<ISelectOptions> => {
  const res = await axios.get('api/v1/payment/search-filter/merchant-account', data);
  return res.data;
};

export const fetchDictPaymentFormNames = async (data: IFlowData): Promise<ISelectOptions> => {
  const res = await axios.get('api/v1/payment/search-filter/form-names', data);
  return res.data?.map((name: string) => ({ value: name, label: name }));
};

export const fetchDictPaymentSystemGroupForm = async (data: IFlowData): Promise<ISelectOptions[]> => {
  const res = await axios.get('api/v1/payment/search-filter/system-group-form', data);
  return res.data;
};

export const checkRefillStatus = async (data: { refillId: number }): Promise<void> => {
  return axios.post('api/v1/refill/init-check-status', data);
};

export const changeRefillStatus = async (data: { refillId: number; targetStatus: string; amount: string | null }): Promise<void> => {
  return await axios.post('api/v1/refill/init-change-status', data);
};

export const resendPaymentCallback = async (data: { paymentOrderId: string }): Promise<void> => {
  return axios.post('api/v1/refill/init-resend-callback', data);
};
